import z from 'zod'
import { pgTable, serial, text, jsonb, timestamp, integer, uniqueIndex } from 'drizzle-orm/pg-core'
import { createSelectSchema } from 'drizzle-zod'
import { pgUser } from './user'
import { pgBrand } from './brand'

// Every platform CASSI can generate content for. Includes generation-only
// targets (e.g. Meta Ads) that have no API connection and are never published —
// see PLATFORM_CAPABILITIES / publishablePlatformTypes for the publishable subset.
export const platformTypes = [
  'linkedin',
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
  'meta-ads',
] as const
export const zPlatformType = z.enum(platformTypes)
export type PlatformType = z.infer<typeof zPlatformType>

/**
 * Per-platform capabilities. `publishable` marks platforms with a real API
 * connection (OAuth, scheduling, analytics, account selection). Generation-only
 * targets like Meta Ads are content containers — excluded from every
 * connect/publish/schedule flow by iterating {@link publishablePlatformTypes}
 * (or guarding with {@link isPublishablePlatform}) rather than the full list.
 */
export const PLATFORM_CAPABILITIES: Record<PlatformType, { publishable: boolean }> = {
  linkedin: { publishable: true },
  instagram: { publishable: true },
  tiktok: { publishable: true },
  youtube: { publishable: true },
  facebook: { publishable: true },
  'meta-ads': { publishable: false },
}

/**
 * Platforms with a real API connection. A subset of {@link platformTypes}, kept
 * as an explicit tuple so it can type the connected-platform DB column and zod
 * enums. Must stay in sync with the `publishable: true` entries above.
 */
export const publishablePlatformTypes = [
  'linkedin',
  'instagram',
  'tiktok',
  'youtube',
  'facebook',
] as const
export const zPublishablePlatformType = z.enum(publishablePlatformTypes)
export type PublishablePlatformType = z.infer<typeof zPublishablePlatformType>

/** Narrowing guard: true for platforms CASSI can actually publish to. */
export const isPublishablePlatform = (p: PlatformType): p is PublishablePlatformType =>
  PLATFORM_CAPABILITIES[p].publishable

/**
 * Outcome of the most recent analytics sync attempt for this platform row.
 *
 * Surfaced to the UI so users see *why* their numbers are stale — three of
 * the four sync failure modes (token refresh, missing scopes, account-level
 * errors) are otherwise invisible (only per-post `syncFailCount` reaches the
 * existing "Sync failing" badge).
 */
export const platformSyncStatuses = [
  'ok',
  'needs_reauth',
  'missing_scopes',
  'rate_limited',
  'error',
] as const
export const zPlatformSyncStatus = z.enum(platformSyncStatuses)
export type PlatformSyncStatus = z.infer<typeof zPlatformSyncStatus>

export const pgPlatform = pgTable(
  'platform',
  {
    id: serial('id').primaryKey(),
    userId: integer('user_id')
      .notNull()
      .references(() => pgUser.id, { onDelete: 'cascade' }),
    brandId: integer('brand_id')
      .notNull()
      .references(() => pgBrand.id, { onDelete: 'cascade' }),

    platform: text('platform', { enum: publishablePlatformTypes }).notNull(),
    authData: jsonb('auth_data').notNull().$type<AuthData>(),
    accounts: jsonb('accounts').notNull().default([]).$type<Accounts>(),

    syncStatus: text('sync_status', { enum: platformSyncStatuses }),
    syncStatusDetail: text('sync_status_detail'),
    syncStatusAt: timestamp('sync_status_at'),

    createdAt: timestamp('created_at').notNull().defaultNow(),
    updatedAt: timestamp('updated_at').notNull().defaultNow(),
  },
  (t) => [
    // App code only ever inserts one row per (brandId, platform) — enforce
    // it at the DB so two concurrent OAuth completions can't both observe
    // "no row" and both insert. Used by `authorize.ts`'s upsert path.
    uniqueIndex('platform_brand_platform_idx').on(t.brandId, t.platform),
  ],
)

const _zPlatform = createSelectSchema(pgPlatform).extend({
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})
export type Platform = z.infer<typeof _zPlatform>

export const zAccount = z.object({
  id: z.string(),
  displayName: z.string(),
  subline: z.string().optional(),
  imageUrl: z.string().optional(),
  /**
   * Per-account access token. Used by platforms (e.g. Facebook Pages) where each
   * connected account has its own publishing token rather than a single brand-level
   * token. Optional because most platforms don't need it.
   */
  accessToken: z.string().optional(),
  /** When true, the 6h sync cron will auto-import new posts for this account */
  autoImport: z.boolean().optional(),
})
export const zAccounts = z.array(zAccount)

export type Account = z.infer<typeof zAccount>
export type Accounts = z.infer<typeof zAccounts>

export const zInstagramRefresh = z.object({
  accessToken: z.string(),
  expiresAt: z.number(),
  scopes: z.array(z.string()).optional(),
})

export const zLinkedInRefresh = z.object({
  accessToken: z.string(),
  expiresAt: z.number(),
  refreshToken: z.string(),
  refreshTokenExpiresAt: z.number(),
  scopes: z.array(z.string()).optional(),
})

export const zTikTokRefresh = z.object({
  accessToken: z.string(),
  expiresAt: z.number(),
  refreshToken: z.string(),
  refreshExpiresAt: z.number(),
  openId: z.string(),
  scopes: z.array(z.string()).optional(),
})

export const zYouTubeRefresh = z.object({
  accessToken: z.string(),
  expiresAt: z.number(),
  refreshToken: z.string(),
  scopes: z.array(z.string()).optional(),
})

/**
 * Facebook stores the long-lived USER token here. Per-page tokens (the things
 * actually used to publish) live on the individual {@link Account} entries in
 * `accounts[].accessToken`. The user token is what we re-exchange via
 * `fb_exchange_token` to keep things alive past 60 days.
 */
export const zFacebookRefresh = z.object({
  accessToken: z.string(),
  expiresAt: z.number(),
  scopes: z.array(z.string()).optional(),
})

export type LinkedInRefresh = z.infer<typeof zLinkedInRefresh>
export type InstagramRefresh = z.infer<typeof zInstagramRefresh>
export type TikTokRefresh = z.infer<typeof zTikTokRefresh>
export type YouTubeRefresh = z.infer<typeof zYouTubeRefresh>
export type FacebookRefresh = z.infer<typeof zFacebookRefresh>

export const zAuthData = zLinkedInRefresh
  .or(zInstagramRefresh)
  .or(zTikTokRefresh)
  .or(zYouTubeRefresh)
  .or(zFacebookRefresh)
export type AuthData = z.infer<typeof zAuthData>
